import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import "./WorkflowTransition.css";

function WorkflowTransition({
  workflowName,
  selectedMUM,
  role,
  userIds,
  onComplete,
}) {
  const [steps, setSteps] = useState([]);
  const [error, setError] = useState(null);

  // Define the SharePoint workflows
  const sharePointWorkflows = ["Promote Now", "Store Transfer"];
  const requiresSharePoint = sharePointWorkflows.includes(workflowName);

  useEffect(() => {
    // Initialize steps based on workflow type
    if (requiresSharePoint) {
      // Full steps for SharePoint workflows
      setSteps([
        { name: "Workflow Initiated", completed: false },
        { name: "HR Ticket Created", completed: false },
        { name: "Ticket Created Successfully", completed: false },
        { name: "Bench Updated", completed: false },
        { name: "Workflow Finished", completed: false },
      ]);
    } else {
      // Simplified steps for other workflows
      setSteps([
        { name: "Workflow Initiated", completed: false },
        { name: "Bench Updated", completed: false },
        { name: "Workflow Finished", completed: false },
      ]);
    }
  }, [workflowName, requiresSharePoint]);

  useEffect(() => {
    if (steps.length === 0) return; // Wait for steps to be initialized

    let cancelled = false;

    const completeAllSteps = async () => {
      // Step 1: Workflow Initiated (immediate)
      if (cancelled) return;
      setSteps((prev) => {
        const updated = [...prev];
        updated[0].completed = true;
        return updated;
      });

      if (requiresSharePoint) {
        // Wait for SharePoint operation to start
        await new Promise((resolve) => setTimeout(resolve, 3000));

        // Step 2: HR Ticket Created (after delay)
        if (cancelled) return;
        setSteps((prev) => {
          const updated = [...prev];
          updated[1].completed = true;
          return updated;
        });

        // Check if the SharePoint item was created successfully
        try {
          await new Promise((resolve) => setTimeout(resolve, 1500)); // Replace with real API call

          // For production, use actual API call:
          /*
          const token = localStorage.getItem("authToken");
          const response = await fetch(`https://57.154.240.131/api/check-sharepoint-status`, {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              workflow_id: workflowName === "Promote Now" ? 8 : 10,
              user_ids: userIds.split(',')
            })
          });
          
          const data = await response.json();
          if (!data.success) {
            throw new Error('SharePoint integration failed: ' + data.message);
          }
          */

          // Step 3: Ticket Created Successfully
          if (cancelled) return;
          setSteps((prev) => {
            const updated = [...prev];
            updated[2].completed = true;
            return updated;
          });
        } catch (err) {
          setError(
            "Failed to create HR ticket in SharePoint. Please try again or contact support."
          );
          return;
        }

        // Wait for next step
        await new Promise((resolve) => setTimeout(resolve, 1500));

        // Step 4: Bench Updated
        if (cancelled) return;
        setSteps((prev) => {
          const updated = [...prev];
          updated[3].completed = true;
          return updated;
        });

        // Wait for next step
        await new Promise((resolve) => setTimeout(resolve, 1500));

        // Step 5: Workflow Finished
        if (cancelled) return;
        setSteps((prev) => {
          const updated = [...prev];
          updated[4].completed = true;
          return updated;
        });
      } else {
        // Simplified flow for non-SharePoint workflows
        
        // Wait a bit before updating bench
        await new Promise((resolve) => setTimeout(resolve, 1500));
        
        // Step 2: Bench Updated
        if (cancelled) return;
        setSteps((prev) => {
          const updated = [...prev];
          updated[1].completed = true;
          return updated;
        });

        // Wait for next step
        await new Promise((resolve) => setTimeout(resolve, 1500));

        // Step 3: Workflow Finished
        if (cancelled) return;
        setSteps((prev) => {
          const updated = [...prev];
          updated[2].completed = true;
          return updated;
        });
      }

      // Wait for 3 seconds on the last step before completing
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Simply complete the workflow without navigation
      if (!cancelled) {
        onComplete();
      }
    };

    completeAllSteps();

    return () => {
      cancelled = true;
    };
  }, [workflowName, selectedMUM, role, userIds, onComplete, steps.length, requiresSharePoint]);

  const content = (
    <div className="workflow-transition-container">
      <div className="workflow-content">
        <h2>Processing {workflowName} Workflow</h2>

        {error ? (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={onComplete}>Return to Management Screen</button>
          </div>
        ) : (
          <div className="workflow-steps">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`workflow-step ${step.completed ? "completed" : ""}`}
              >
                <span className="step-circle">
                  {step.completed ? "✓" : index + 1}
                </span>
                <span className="step-label">{step.name}</span>
                {index < steps.length - 1 && (
                  <div 
                    className={`connector-line ${
                      steps[index + 1]?.completed ? "completed" : ""
                    }`}
                  ></div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}

export default WorkflowTransition;